import { differenceInDays, differenceInMonths } from "date-fns";
import {
  type Subscription,
  type PaymentHistory,
  type LifecycleEvent,
} from "@/types/subscription";

export interface StartDateChangeValidation {
  isValid: boolean;
  impact: {
    paymentsAffected: number;
    periodsAffected: number;
    metricsAffected: string[];
  };
  warnings: string[];
  recommendations: string[];
}

export interface StartDateChangeImpact {
  paymentsBeforeStartDate: PaymentHistory[];
  paymentsAfterStartDate: PaymentHistory[];
  affectedPeriods: number;
  lifecycleEventsAffected: LifecycleEvent[];
  metricsImpacted: string[];
}

/**
 * Validates the impact of changing a subscription's start date
 * (Debug logging removed for production cleanliness)
 */
export function validateStartDateChange(
  subscription: Subscription,
  newStartDate: Date,
): StartDateChangeValidation {
  try {
    // Ensure required arrays exist with defaults
    const safeSubscription = {
      ...subscription,
      paymentHistory: subscription.paymentHistory || [],
      lifecycleEvents: subscription.lifecycleEvents || [],
      subscriptionPeriods: subscription.subscriptionPeriods || [],
    };

    const impact = calculateStartDateImpact(safeSubscription, newStartDate);

    const warnings: string[] = [];
    const recommendations: string[] = [];

    // Generate warnings based on impact
    if (impact.paymentsBeforeStartDate.length > 0) {
      warnings.push(
        `${impact.paymentsBeforeStartDate.length} payments exist before the new start date`,
      );
    }

    if (impact.affectedPeriods > 0) {
      warnings.push(
        `${impact.affectedPeriods} subscription periods will be affected`,
      );
    }

    if (impact.lifecycleEventsAffected.length > 0) {
      warnings.push("Lifecycle events may need re-evaluation");
    }

    // High-risk change heuristics
    const monthsActive = differenceInMonths(
      new Date(),
      safeSubscription.startDate,
    );
    const paymentCount = safeSubscription.paymentHistory.length;
    const hasReactivations = safeSubscription.lifecycleEvents.some(
      (e) => e.event === "reactivated",
    );

    if (paymentCount > 12) {
      warnings.push(
        `High payment volume (${paymentCount} payments) will be affected`,
      );
    }

    if (hasReactivations) {
      warnings.push("Reactivation patterns will be affected");
    }

    if (monthsActive > 24) {
      warnings.push("Long-term subscription metrics will change significantly");
    }

    // Recommendations
    if (impact.paymentsBeforeStartDate.length > 0) {
      recommendations.push(
        "Consider if these early payments should be removed or reclassified",
      );
    }

    if (warnings.length === 0) {
      recommendations.push("This change appears safe with minimal impact");
    } else {
      recommendations.push("Consider creating a backup before proceeding");
    }

    return {
      isValid:
        warnings.length === 0 ||
        warnings.every((w) => !w.includes("High payment volume")),
      impact: {
        paymentsAffected:
          impact.paymentsBeforeStartDate.length +
          impact.paymentsAfterStartDate.length,
        periodsAffected: impact.affectedPeriods,
        metricsAffected: impact.metricsImpacted,
      },
      warnings,
      recommendations,
    };
  } catch {
    // Silent fallback: return safe default (no debug noise)
    return {
      isValid: true,
      impact: {
        paymentsAffected: 0,
        periodsAffected: 0,
        metricsAffected: [],
      },
      warnings: [],
      recommendations: ["Validation temporarily bypassed due to error"],
    };
  }
}

/**
 * Calculates the detailed impact of changing a subscription's start date
 * (Debug logging removed)
 */
export function calculateStartDateImpact(
  subscription: Subscription,
  newStartDate: Date,
): StartDateChangeImpact {
  try {
    // Ensure arrays exist with safe defaults
    const paymentHistory = subscription.paymentHistory || [];
    const lifecycleEvents = subscription.lifecycleEvents || [];
    const subscriptionPeriods = subscription.subscriptionPeriods || [];

    // Find payments that would be affected
    const paymentsBeforeStartDate = paymentHistory.filter((payment) => {
      try {
        const paymentDate = payment.date || (payment as any).payment_date;
        return paymentDate && new Date(paymentDate) < newStartDate;
      } catch {
        return false;
      }
    });

    const paymentsAfterStartDate = paymentHistory.filter((payment) => {
      try {
        const paymentDate = payment.date || (payment as any).payment_date;
        return paymentDate && new Date(paymentDate) >= newStartDate;
      } catch {
        return false;
      }
    });

    // Calculate affected periods
    const affectedPeriods = subscriptionPeriods.filter((period) => {
      try {
        const periodStart = new Date(period.startDate);
        return periodStart.getTime() !== newStartDate.getTime();
      } catch {
        return false;
      }
    }).length;

    // Lifecycle events near new start date
    const lifecycleEventsAffected = lifecycleEvents.filter((event) => {
      try {
        const eventDate = new Date(event.date);
        return Math.abs(differenceInDays(eventDate, newStartDate)) < 90;
      } catch {
        return false;
      }
    });

    const metricsImpacted: string[] = [];

    if (paymentsBeforeStartDate.length > 0) {
      metricsImpacted.push("Payment reconciliation status");
      metricsImpacted.push("Average payment amounts");
    }

    if (affectedPeriods > 0) {
      metricsImpacted.push("Subscription period calculations");
      metricsImpacted.push("Active/inactive periods");
    }

    if (lifecycleEventsAffected.length > 0) {
      metricsImpacted.push("Lifecycle event analysis");
      metricsImpacted.push("Reactivation patterns");
    }

    metricsImpacted.push("Subscription tenure");
    metricsImpacted.push("Days active calculation");

    return {
      paymentsBeforeStartDate,
      paymentsAfterStartDate,
      affectedPeriods,
      lifecycleEventsAffected,
      metricsImpacted,
    };
  } catch {
    return {
      paymentsBeforeStartDate: [],
      paymentsAfterStartDate: [],
      affectedPeriods: 0,
      lifecycleEventsAffected: [],
      metricsImpacted: ["Error during calculation"],
    };
  }
}

/**
 * Checks if a start date change should be allowed based on subscription maturity
 */
export function shouldAllowStartDateChange(subscription: Subscription): {
  allowed: boolean;
  reason: string;
} {
  const monthsActive = differenceInMonths(new Date(), subscription.startDate);
  const paymentCount = (subscription.paymentHistory || []).length;
  const hasReactivations = (subscription.lifecycleEvents || []).some(
    (e) => e.event === "reactivated",
  );

  if (monthsActive < 1 && paymentCount < 3) {
    return { allowed: true, reason: "New subscription with minimal history" };
  }

  if (paymentCount > 24 || (hasReactivations && monthsActive > 12)) {
    return {
      allowed: false,
      reason: "High-risk change requiring explicit confirmation",
    };
  }

  return { allowed: true, reason: "Change appears safe" };
}

/**
 * Gets a human-readable summary of the start date change impact
 */
export function getStartDateChangeSummary(
  subscription: Subscription,
  newStartDate: Date,
): string {
  const validation = validateStartDateChange(subscription, newStartDate);
  const daysDifference = differenceInDays(newStartDate, subscription.startDate);

  if (validation.isValid && validation.warnings.length === 0) {
    return `Safe change: ${Math.abs(daysDifference)} days ${
      daysDifference > 0 ? "later" : "earlier"
    }`;
  }

  return `Caution: ${validation.warnings.length} warnings, ${validation.impact.paymentsAffected} payments affected`;
}
